import React from 'react';
import { LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import StatTile from '../shared/StatTile';
import { FiFileText, FiBriefcase, FiDollarSign, FiTrendingUp, FiGlobe, FiHome, FiCheckCircle, FiAlertCircle, FiArrowUp, FiArrowDown } from 'react-icons/fi';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const fmtW = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K MT`;
  return `${v.toFixed(1)} MT`;
};

const YoY = ({ current, previous }) => {
  if (!previous || previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  const up  = pct >= 0;
  return (
    <span className={`tw-inline-flex tw-items-center tw-gap-0.5 tw-text-xs tw-font-semibold ${up ? 'tw-text-emerald-600' : 'tw-text-red-500'}`}>
      {up ? <FiArrowUp size={10} /> : <FiArrowDown size={10} />}
      {Math.abs(pct).toFixed(1)}% YoY
    </span>
  );
};
YoY.propTypes = {
  current: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  previous: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

// Thin wrapper that keeps the drill-down click behavior while the tile
// visuals live in the shared StatTile.
const ClickableTile = ({ onClick, children }) => {
  if (!onClick) return <div className="tw-h-full">{children}</div>;
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      className="tw-h-full tw-rounded tw-cursor-pointer tw-transition-all hover:tw-shadow-md active:tw-scale-[0.99]"
    >
      {children}
    </div>
  );
};
ClickableTile.propTypes = { onClick: PropTypes.func, children: PropTypes.node };

const ContractSummaryCards = ({ data, loading, onDrillDown, onShowUnreleased }) => {
  if (loading) {
    return (
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="tw-bg-white tw-rounded tw-shadow-sm tw-p-4 tw-border tw-border-gray-100">
            <div className="tw-flex tw-justify-center tw-items-center tw-py-6"><LoadingSpinner size="sm" /></div>
          </div>
        ))}
      </div>
    );
  }

  const d = data || {};

  const cards = [
    {
      label:      'Total Contracts',
      value:      fmt(d.total_contracts),
      sub:        `+${fmt(d.total_addendums)} addendums`,
      yoyCurrent: d.total_contracts,
      yoyPrev:    d.yoy?.prev_total_contracts,
      icon:       FiFileText,
      theme:      'blue',
      onClick:    onDrillDown && d.total_contracts > 0
                    ? () => onDrillDown('Total Contracts', {})
                    : undefined
    },
    {
      label:      'Projects',
      value:      fmt(d.project_count),
      sub:        `across ${fmt(d.total_contracts)} contracts`,
      yoyCurrent: null,
      yoyPrev:    null,
      icon:       FiBriefcase,
      theme:      'indigo',
      onClick:    undefined
    },
    {
      label:      'Total Value',
      value:      fmtM(d.grand_total_value),
      sub:        `Contract ${fmtM(d.total_value)} + Addendum ${fmtM(d.addendum_value)}`,
      yoyCurrent: d.grand_total_value,
      yoyPrev:    d.yoy?.prev_total_value,
      icon:       FiDollarSign,
      theme:      'green',
      onClick:    undefined
    },
    {
      label:      'Total Weight',
      value:      fmtW((d.total_weight || 0) + (d.addendum_weight || 0)),
      sub:        `Contract ${fmtW(d.total_weight)} + Addendum ${fmtW(d.addendum_weight)}`,
      yoyCurrent: d.total_weight,
      yoyPrev:    d.yoy?.prev_total_weight,
      icon:       FiTrendingUp,
      theme:      'cyan',
      onClick:    undefined
    },
    {
      label:      'Released',
      value:      fmt(d.released_count),
      sub:        `${fmt(d.pending_count)} pending`,
      yoyCurrent: null,
      yoyPrev:    null,
      icon:       FiCheckCircle,
      theme:      'green',
      onClick:    onDrillDown && d.released_count > 0
                    ? () => onDrillDown('Released Contracts', {})
                    : undefined
    },
    {
      label:      'Unreleased',
      value:      fmt(d.unreleased_count),
      sub:        `${fmt(d.unreleased_contracts)} contracts + ${fmt(d.unreleased_addendums)} addendums`,
      yoyCurrent: null,
      yoyPrev:    null,
      icon:       FiAlertCircle,
      theme:      'amber',
      onClick:    onShowUnreleased && d.unreleased_count > 0
                    ? () => onShowUnreleased()
                    : undefined
    },
    {
      label:      'Domestic',
      value:      fmt(d.domestic_count),
      sub:        fmtM(d.domestic_value),
      yoyCurrent: null,
      yoyPrev:    null,
      icon:       FiHome,
      theme:      'amber',
      onClick:    onDrillDown && d.domestic_count > 0
                    ? () => onDrillDown('Domestic Contracts', { type_of_sales: 'Domestic' })
                    : undefined
    },
    {
      label:      'Overseas',
      value:      fmt(d.oversea_count),
      sub:        fmtM(d.oversea_value),
      yoyCurrent: null,
      yoyPrev:    null,
      icon:       FiGlobe,
      theme:      'violet',
      onClick:    onDrillDown && d.oversea_count > 0
                    ? () => onDrillDown('Overseas Contracts', { type_of_sales: 'Oversea' })
                    : undefined
    }
  ];

  return (
    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4">
      {cards.map(({ yoyCurrent, yoyPrev, onClick, sub, ...c }) => (
        <ClickableTile key={c.label} onClick={onClick}>
          <StatTile
            {...c}
            sub={onClick ? (
              <>
                {sub}
                <span className="tw-block tw-text-blue-500 tw-font-medium">Click to view details →</span>
              </>
            ) : sub}
            trend={<YoY current={yoyCurrent} previous={yoyPrev} />}
          />
        </ClickableTile>
      ))}
    </div>
  );
};

ContractSummaryCards.propTypes = {
  data:             PropTypes.object,
  loading:          PropTypes.bool,
  onDrillDown:      PropTypes.func,
  onShowUnreleased: PropTypes.func
};
ContractSummaryCards.defaultProps = { data: null, loading: false, onDrillDown: null, onShowUnreleased: null };

export default ContractSummaryCards;
